import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { Modality } from '~/domain/dicom/modality';
import type { TypedRoi } from '~/hooks/workflows/backend/tasks/roiOverlapMatch';
import { useBestMatchingContourModel } from '~/hooks/workflows/backend/tasks/useBestMatchingContourModel';
import { contourPropagationTaskState } from '~/state/atoms/adapt/contourPropagationTaskAtom';
import type { ContourModelChoice } from '~/state/atoms/adaptiveReplanningAtoms';
import { usePropagationRunning } from './usePropagationRun';

export interface ComparisonModelChoice {
    // Every model and customization, best match first.
    readonly ranking: readonly ContourModelChoice[];
    readonly unavailable?: 'signedOut' | 'failed';
}

// Keeps the task's Contour+ model on the best match for the current source structures, unless the user chose one;
// held while a run is in flight.
export function useComparisonModelChoice(
    sourceRois: readonly TypedRoi[],
    modality: Modality
): ComparisonModelChoice {
    const best = useBestMatchingContourModel(sourceRois, modality);
    const isPropagating = usePropagationRunning();
    const [task, setTask] = useRecoilState(contourPropagationTaskState);
    const chosenByUser = !!task.comparisonModelChosen;

    useEffect(() => {
        if (!best.settled || isPropagating || chosenByUser) {
            return;
        }
        const choice = best.choice;
        setTask((current) =>
            current.comparisonModel?.modelName === choice?.modelName &&
            current.comparisonModel?.customizationName ===
                choice?.customizationName
                ? current
                : { ...current, comparisonModel: choice }
        );
    }, [best, isPropagating, chosenByUser, setTask]);

    return { ranking: best.ranking, unavailable: best.unavailable };
}
